import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { debug } from '@ember/debug';
import { task } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div class=\'{{if @editable \"beebee-thumbnail-editable\"}}\' ...attributes>\n  {{#if (and @item @item.thumbnail_url)}}\n    {{#if (is-image @item.thumbnail_type)}}\n      <div\n        class=\'beebee-thumbnail beebee-thumbnail-image beebee-thumbnail-size-{{@size}}\'\n        style=\'{{css-style \"background-image\" this.thumbnailUrl}}\'\n      >\n        &nbsp;\n      </div>\n    {{/if}}\n  {{else if @item}}\n    {{#if @item.firstLetter}}\n      <div class=\'beebee-thumbnail letter-{{@item.firstLetter}} beebee-thumbnail-size-{{@size}}\'>\n        {{#if @item.firstLetters}}\n          {{@item.firstLetters}}\n        {{else}}\n          &nbsp;\n        {{/if}}\n      </div>\n    {{else if @item.thumbnail_icon}}\n      <FaIcon @icon={{@item.thumbnail_icon.icon}} class={{@item.thumbnail_icon.class}} />\n\n      {{#if @item.thumbnail_sub_icon}}\n        <FaIcon @icon={{@item.thumbnail_sub_icon.icon}} class={{@item.thumbnail_sub_icon.class}} />\n      {{/if}}\n    {{/if}}\n  {{/if}}\n\n  {{#if @editable}}\n    <button\n      type=\'button\'\n      class=\'beebee-thumbnail-edit-button\'\n      aria-label={{t \'change thumbnail image\'}}\n      data-test-open-upload-modal\n      {{on \'click\' this.openUploadModal}}\n    >\n      <FaIcon @icon=\'pen\' />\n      <EmberTooltip @popperContainer=\'body\'>\n        {{t \'change thumbnail image\'}}\n      </EmberTooltip>\n    </button>\n\n    {{! FileUploader lives in beebee-component-file (resolved loosely at the\n        app level): only enable @editable from consumers whose app includes\n        that addon. in-element (insertBefore=null appends instead of\n        replacing) portals the modal under <body>. }}\n    {{#if this.showUploadModal}}\n      {{#in-element this.modalDestination insertBefore=null}}\n        <BbModalModern\n          @isOpen={{this.showUploadModal}}\n          @title={{t \'change thumbnail image\'}}\n          @busy={{this.removeThumbnailTask.isRunning}}\n          @onClose={{this.closeUploadModal}} data-test-close-upload-modal\n        >\n          <:main>\n            <div class=\'beebee-thumbnail-upload-modal\'>\n              {{#if (and @item.thumbnail_url (is-image @item.thumbnail_type))}}\n                <div class=\'beebee-thumbnail-upload-current\'>\n                  <div\n                    class=\'beebee-thumbnail beebee-thumbnail-image beebee-thumbnail-size-48\'\n                    style=\'{{css-style \"background-image\" this.thumbnailUrl}}\'\n                  >\n                    &nbsp;\n                  </div>\n\n                  <div class=\'beebee-thumbnail-upload-current-info\'>\n                    <span class=\'beebee-thumbnail-upload-current-label\'>\n                      {{t \'current thumbnail image\'}}\n                    </span>\n\n                    <button\n                      type=\'button\'\n                      class=\'beebee-thumbnail-upload-remove\'\n                      disabled={{this.removeThumbnailTask.isRunning}}\n                      data-test-remove-thumbnail\n                      {{on \'click\' (perform this.removeThumbnailTask)}}\n                    >\n                      <FaIcon @icon=\'trash-alt\' />\n                      {{t \'remove thumbnail image\'}}\n                    </button>\n                  </div>\n                </div>\n              {{/if}}\n\n              <p class=\'beebee-thumbnail-upload-hint\'>\n                {{t \'thumbnail upload hint\'}}\n              </p>\n\n              <FileUploader\n                @applicationController={{@applicationController}}\n                @item={{@item}}\n                @saveThumbnail={{true}}\n                @accept=\'image/*\'\n                @action={{this.onFileUploaded}} data-test-on-file-uploaded\n              />\n            </div>\n          </:main>\n        </BbModalModern>\n      {{/in-element}}\n    {{/if}}\n  {{/if}}\n</div>");

var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
let BbThumbnailComponent = (_dec = task({
  drop: true
}), _dec2 = task({
  drop: true
}), _class = class BbThumbnailComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    _initializerDefineProperty(this, "notify", _descriptor2, this);
    _initializerDefineProperty(this, "intl", _descriptor3, this);
    _initializerDefineProperty(this, "showUploadModal", _descriptor4, this);
  }
  // Portal destination for the upload modal: rendered under <body> so the
  // fixed-position overlay escapes ancestor overflow/transform contexts.
  get modalDestination() {
    return document.body;
  }
  get thumbnailUrl() {
    const thumbnailUrl = this.args.item?.thumbnail_api_url;
    if (thumbnailUrl) {
      return `url(${this.args.item.thumbnail_api_url}-${this.args.thumbnail_size}.jpeg)`;
    }
    return '';
  }
  openUploadModal() {
    this.showUploadModal = true;
  }
  closeUploadModal() {
    this.showUploadModal = false;
  }

  // Removing only unsets the thumbnail_* fields on the record (same pattern as
  // file-view's setThumbnailTask): the underlying file stays among the item's
  // files, it just stops being the profile image.
  // drop: one removal at a time; saveRecord already notifies success/failure.
  *removeThumbnailTask() {
    const item = this.args.item;
    item.thumbnail_url = null;
    item.thumbnail_name = null;
    item.thumbnail_etag = null;
    item.thumbnail_type = null;
    try {
      yield this.args.applicationController.saveRecord(item);
    } catch (e) {
      debug('removeThumbnailTask error:', e);
    }
  }

  // FileUploader @action callback. The upload itself already sets the file as
  // the item's default thumbnail (@saveThumbnail → thumbnail=true, handled
  // server-side); here we only need to make the file public so the thumbnail
  // is reachable without authentication.
  onFileUploaded(itemId, response) {
    this.makePublicTask.perform(response);
  }

  // drop: one permission change at a time
  *makePublicTask(response) {
    // Direct uploads answer with an array of file objects, presigned uploads
    // with a single object — normalize before reading id/urls.
    const file = Array.isArray(response) ? response[0] : response;
    try {
      if (!file?.id || !file?.file_url) {
        throw new Error('upload response missing file id/url');
      }
      const body = new URLSearchParams({
        file_url: file.file_url,
        thumbnail_url: file.thumbnail_url || '',
        file_id: file.id,
        public: true
      });
      const res = yield fetch(`${this.args.applicationController.urlApi}/app/change_permission_file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.session.token}`
        },
        body
      });
      if (!res.ok) {
        throw new Error(`change_permission_file: ${res.status}`);
      }
      if (this.isDestroying || this.isDestroyed) return;
      this.notify.success(this.intl.t('save done'));
    } catch (e) {
      debug('makePublicTask error:', e);
      if (!(this.isDestroying || this.isDestroyed)) {
        // Upload succeeded (the thumbnail is set) — only the public toggle
        // failed, so warn instead of erroring.
        this.notify.warning(this.intl.t('operation not permitted'));
      }
    } finally {
      if (!(this.isDestroying || this.isDestroyed)) {
        this.showUploadModal = false;
      }
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "showUploadModal", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "openUploadModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "openUploadModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeUploadModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeUploadModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeThumbnailTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "removeThumbnailTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onFileUploaded", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onFileUploaded"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "makePublicTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "makePublicTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbThumbnailComponent);

export { BbThumbnailComponent as default };
//# sourceMappingURL=bb-thumbnail.js.map
